import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CampaignEmailRichTextEditor from './CampaignEmailRichTextEditor';
import CampaignEmailLivePreview, {
  CampaignEmailVariablesLegend,
} from './CampaignEmailLivePreview';
import ProductCardEmailSettingsFields from './ProductCardEmailSettingsFields';
import {
  campaignEmailTemplateBannerImageUrl,
  saveCampaignEmailTemplate,
  uploadCampaignEmailBanner,
} from '../../api/campaigns';
import {
  campaignEmailTemplateId,
  HUBUP_NOTIFICATION_BUTTON_VARIABLE,
  buildEditorHubupPreviewFromDesign,
  templateUsesHubupVariable,
} from '../../utils/campaignEmailTemplate';
import {
  EMAIL_BANNER_SIZE_TOOLTIP,
  hubupButtonDesignFromSource,
  productCardOptionsFromSource,
  productCardOptionsToEmailFields,
  defaultProductCardOptions,
  defaultHubupButtonDesign,
  hubupButtonDesignToEmailFields,
} from '../../utils/emailButtonStyles';
import EmailButtonDesignFields from './EmailButtonDesignFields';
import InfoTooltip from '../ui/InfoTooltip';
import Button from '../ui/Button';
import Header from '../ui/Header';
import Breadcrumb from '../ui/Breadcrumb';
import {
  campaignHasProducts,
  campaignHubupConfigured,
} from '../../utils/campaignCapabilities';

const TABS = [
  { id: 'identity', label: 'Identité' },
  { id: 'message', label: 'Message' },
  { id: 'options', label: 'Options' },
];

function readInitialFromTemplate(template) {
  if (!template) {
    return {
      name: '',
      subject: '',
      html: '',
      bannerUrl: '',
      bannerKey: '',
      bannerOriginalName: '',
      templateId: '',
      productCardOptions: defaultProductCardOptions(),
    };
  }
  return {
    name: template.name || '',
    subject: template.emailTemplateSubject || '',
    html: template.emailTemplateHtml || '',
    bannerUrl: template.emailTemplateBannerUrl || '',
    bannerKey: template.emailTemplateBannerKey || '',
    bannerOriginalName: template.emailTemplateBannerOriginalName || '',
    templateId: campaignEmailTemplateId(template),
    productCardOptions: productCardOptionsFromSource(template),
  };
}

export default function CampaignEmailTemplateEditorWizard({
  campaign,
  initialTemplate = null,
  onClose,
  onSaved,
  layout = 'page',
}) {
  const navigate = useNavigate();
  const initial = useMemo(() => readInitialFromTemplate(initialTemplate), [initialTemplate]);

  const [tab, setTab] = useState('identity');
  const [name, setName] = useState(initial.name);
  const [subject, setSubject] = useState(initial.subject);
  const [html, setHtml] = useState(initial.html);
  const [bannerUrl, setBannerUrl] = useState(initial.bannerUrl);
  const [bannerKey, setBannerKey] = useState(initial.bannerKey);
  const [bannerOriginalName, setBannerOriginalName] = useState(initial.bannerOriginalName);
  const [templateId, setTemplateId] = useState(initial.templateId);
  const [productCardOptions, setProductCardOptions] = useState(initial.productCardOptions);
  const [hubupButtonDesign, setHubupButtonDesign] = useState(() =>
    hubupButtonDesignFromSource(initialTemplate, campaign),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const hasProducts = campaignHasProducts(campaign);
  const hubupConfigured = campaignHubupConfigured(campaign);
  const usesHubupInEmail = useMemo(
    () => hubupConfigured && templateUsesHubupVariable(html),
    [html, hubupConfigured],
  );
  const hubupPreviewNotification = useMemo(() => {
    if (!usesHubupInEmail) return null;
    return buildEditorHubupPreviewFromDesign(hubupButtonDesign);
  }, [usesHubupInEmail, hubupButtonDesign]);

  useEffect(() => {
    const next = readInitialFromTemplate(initialTemplate);
    setName(next.name);
    setSubject(next.subject);
    setHtml(next.html);
    setBannerUrl(next.bannerUrl);
    setBannerKey(next.bannerKey);
    setBannerOriginalName(next.bannerOriginalName);
    setTemplateId(next.templateId);
    setProductCardOptions(next.productCardOptions);
    setHubupButtonDesign(
      hubupButtonDesignFromSource(initialTemplate, campaign) || defaultHubupButtonDesign(),
    );
    setTab('identity');
  }, [initialTemplate, campaign]);

  const campaignId = campaign?._id || campaign?.id;
  const bannerPreviewUrl =
    templateId && bannerKey
      ? campaignEmailTemplateBannerImageUrl(campaign, {
          _id: templateId,
          emailTemplateBannerUrl: bannerUrl,
          emailTemplateBannerKey: bannerKey,
          emailTemplateBannerOriginalName: bannerOriginalName,
        })
      : bannerUrl;

  const handleClose = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const handleSave = async () => {
    if (!campaignId || !name.trim()) {
      setError('Indiquez un nom de template.');
      setTab('identity');
      return;
    }
    if (!String(html || '').replace(/<[^>]+>/g, '').trim()) {
      setError('Le contenu du message est requis.');
      setTab('message');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveCampaignEmailTemplate(campaignId, {
        templateId: templateId || null,
        name: name.trim(),
        emailTemplateSubject: subject,
        emailTemplateHtml: html,
        emailTemplateBannerUrl: bannerUrl || null,
        emailTemplateBannerKey: bannerKey || null,
        emailTemplateBannerOriginalName: bannerOriginalName || null,
        ...productCardOptionsToEmailFields(productCardOptions),
        ...(usesHubupInEmail
          ? {
              ...hubupButtonDesignToEmailFields(hubupButtonDesign),
              hubupEmailNotificationMessageId: null,
              hubupEmailNotificationTitle: null,
              hubupEmailNotificationFormId: null,
            }
          : {
              hubupEmailNotificationMessageId: null,
              hubupEmailNotificationTitle: null,
              hubupEmailNotificationFormId: null,
            }),
      });
      onSaved?.(res?.data?.campaign);
      handleClose();
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const editorBody = (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
      <div className="space-y-4 min-w-0">
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'identity' ? (
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <label className="block text-sm font-medium text-foreground">Nom du template</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm"
              placeholder="Ex. Relance J+3"
            />
            <p className="text-xs text-muted-foreground">
              Ce nom apparaît dans la bibliothèque de la campagne.
            </p>
          </div>
        ) : null}

        {tab === 'message' ? (
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <CampaignEmailVariablesLegend showHubup={hubupConfigured} />
            {hubupConfigured ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setHtml((cur) => {
                      const base = String(cur || '').trim();
                      if (base.includes(HUBUP_NOTIFICATION_BUTTON_VARIABLE)) return cur;
                      return `${base}<p>${HUBUP_NOTIFICATION_BUTTON_VARIABLE}</p>`;
                    });
                    setTab('message');
                  }}
                >
                  Insérer {HUBUP_NOTIFICATION_BUTTON_VARIABLE}
                </Button>
              </div>
            ) : null}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Objet</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Contenu</label>
              <CampaignEmailRichTextEditor
                value={html}
                onChange={setHtml}
                minEditorHeight={180}
              />
            </div>
            {usesHubupInEmail ? (
              <EmailButtonDesignFields
                title="Bouton HubUP (notification)"
                titleTooltip="Style du bouton à l’emplacement de la variable dans le texte. La notification publiée se choisit à l’envoi du mail."
                value={hubupButtonDesign}
                onChange={setHubupButtonDesign}
              />
            ) : null}
          </div>
        ) : null}

        {tab === 'options' ? (
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            {hubupConfigured && !usesHubupInEmail ? (
              <p className="text-sm text-muted-foreground">
                Insérez %hubup_notification_button% dans le message (onglet Message) pour
                configurer le bouton HubUP.
              </p>
            ) : null}
            {!hasProducts ? (
              <p className="text-sm text-muted-foreground">
                Ajoutez des produits dans l&apos;onglet Produits de la campagne pour proposer une
                fiche produit dans l&apos;email.
              </p>
            ) : (
              <ProductCardEmailSettingsFields
                value={productCardOptions}
                onChange={setProductCardOptions}
              />
            )}
            <div>
              <label className="mb-1 flex items-center gap-1 text-sm font-medium text-foreground">
                Bandeau
                <InfoTooltip text={EMAIL_BANNER_SIZE_TOOLTIP} />
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file || !campaignId) return;
                  const res = await uploadCampaignEmailBanner(campaignId, file);
                  const c = res?.data?.campaign;
                  setBannerUrl(c?.emailTemplateBannerUrl || '');
                  setBannerKey(c?.emailTemplateBannerKey || '');
                  setBannerOriginalName(c?.emailTemplateBannerOriginalName || '');
                }}
                className="w-full text-sm"
              />
            </div>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <CampaignEmailLivePreview
        emailPreviewMode="send"
        subject={subject}
        html={html}
        bannerUrl={bannerPreviewUrl}
        hubupButtonDesign={hubupButtonDesign}
        hubupNotification={usesHubupInEmail ? hubupPreviewNotification : null}
        productCardOptions={hasProducts ? productCardOptions : undefined}
      />
    </div>
  );

  const editorChrome = (
    <>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Template email</p>
          <h1 className="text-2xl font-bold text-foreground">
            {templateId ? 'Modifier le template' : 'Nouveau template'}
          </h1>
          {layout !== 'embed' ? (
            <p className="text-sm text-muted-foreground">{campaign?.name}</p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            type="button"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
      {editorBody}
    </>
  );

  if (layout === 'modal') {
    return null;
  }

  if (layout === 'embed') {
    return <div className="w-full min-w-0">{editorChrome}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />
      <main className="main-content max-w-6xl mx-auto">{editorChrome}</main>
    </div>
  );
}
